import * as React from 'react';
import Editor from 'pureUi/Editor';
import { removeAllLineBreaks } from 'utils/string';

export interface IBreakdownRichTextAreaProps {
  className?: string;
  title: React.ReactNode;
  text: string;
  onChange: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const BreakdownRichTextArea: React.FC<IBreakdownRichTextAreaProps> = props => {
  // Track the initial value that was loaded from the backend
  // This prevents the initial HTML conversion from being treated as a user edit
  const initialTextRef = React.useRef<string | null>(null);
  const isFirstChangeRef = React.useRef<boolean>(true);

  // Store the initial text value when it first loads from the backend
  React.useEffect(() => {
    if (initialTextRef.current === null && props.text) {
      // Store the initial value (whether HTML or plain text)
      initialTextRef.current = removeAllLineBreaks(props.text);
    }
  }, [props.text]);

  const handleEditorChange = React.useCallback(
    (content: string) => {
      // by default, editor components return their HTML with line breaks
      const newContent = removeAllLineBreaks(content);

      // On the very first onChange event after the component loads data,
      // check if the editor is just converting plain text to HTML
      if (isFirstChangeRef.current && initialTextRef.current !== null) {
        isFirstChangeRef.current = false;

        // If the new content is just the HTML version of the initial plain text,
        // update our reference but don't trigger the onChange callback
        // This prevents marking the breakdown as edited when the editor is just
        // doing its initial plain text -> HTML conversion
        if (newContent !== initialTextRef.current) {
          // The editor converted the text to HTML - update our baseline
          initialTextRef.current = newContent;
          return;
        }
      }

      // For all subsequent changes, check if content actually changed
      if (newContent !== props.text) {
        props.onChange(newContent);
      }
    },
    [props.onChange, props.text]
  );

  return (
    <div className={`textarea-container ${props.className ?? ''} flex items-start`}>
      <p className="w-[25%] m-0 font-bold text-17px leading-md text-black-true">{props.title}</p>
      <div className="w-full">
        <Editor
          handleEditorChange={handleEditorChange}
          text={props.text}
          options={[]}
          mentionAbility={false}
          disabled={props.disabled || false}
          placeHolder={props.placeholder || 'Enter text here...'}
          minHeight="150px"
          skipInitialChange={true}
          initVals={{
            autoresize_bottom_margin: 0,
            min_height: 150,
          }}
        />
      </div>
    </div>
  );
};

export default BreakdownRichTextArea;
